import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600&display=swap');

  @keyframes ach-notify-in {
    0% { opacity: 0; transform: translateY(40px) scale(0.92); }
    60% { transform: translateY(-6px) scale(1.02); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes ach-notify-out {
    0% { opacity: 1; transform: translateY(0) scale(1); }
    100% { opacity: 0; transform: translateY(20px) scale(0.94); }
}

  @keyframes ach-shimmer {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }

  @keyframes ach-burst {
    0% { transform: scale(0.5); opacity: 1; }
    100% { transform: scale(2.2); opacity: 0; }
  }

  .ach-notify-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ach-notify-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0,0,0,0.55);
    pointer-events: all;
    animation: ach-fade-bg 0.3s ease forwards;
  }

  @keyframes ach-fade-bg {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .ach-notify-backdrop.exiting {
    animation: ach-fade-bg-out 0.35s ease forwards;
  }

  @keyframes ach-fade-bg-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }

  .ach-notify-card {
    position: relative;
    z-index: 1;
    pointer-events: all;
    width: min(320px, 88vw);
    background: #1a1a20;
    border: 1px solid rgba(240,192,64,0.35);
    border-radius: 20px;
    padding: 28px 24px 22px;
    text-align: center;
    box-shadow:
      0 0 0 1px rgba(240,192,64,0.1),
      0 20px 60px rgba(0,0,0,0.6),
      0 0 80px rgba(240,192,64,0.08);
    animation: ach-notify-in 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  .ach-notify-card.exiting {
    animation: ach-notify-out 0.3s ease forwards;
  }

  .ach-notify-label {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 2rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    background: linear-gradient(90deg, #f0c040, #ffe082, #f0c040);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ach-shimmer 2.5s linear infinite;
    margin-bottom: 16px;
  }

  .ach-notify-icon-wrap {
    position: relative;
    width: 80px;
    height: 80px;
    margin: 0 auto 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ach-notify-burst {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid rgba(240,192,64,0.5);
    animation: ach-burst 1s ease-out 0.2s forwards;
    opacity: 0;
  }

  .ach-notify-burst-2 {
    animation-delay: 0.5s;
  }

  .ach-notify-icon-bg {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(240,192,64,0.2) 0%, rgba(240,192,64,0.05) 70%);
    border: 1px solid rgba(240,192,64,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2.2rem;
  }

  .ach-notify-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1.7rem;
    letter-spacing: 0.04em;
    color: #fff;
    line-height: 1.1;
    margin-bottom: 8px;
  }

  .ach-notify-desc {
    font-family: 'DM Sans', sans-serif;
    font-size: 1.1rem;
    color: rgba(255,255,255,0.45);
    line-height: 1.5;
    margin-bottom: 20px;
  }

  .ach-notify-xp {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1.4rem;
    letter-spacing: 0.08em;
    color: #f0c040;
    margin-bottom: 4px;
  }

  .ach-notify-btn {
    font-family: 'DM Sans', sans-serif;
    font-size: 1.2rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: #1a1a20;
    background: linear-gradient(135deg, #f0c040, #e6a800);
    border: none;
    border-radius: 10px;
    padding: 10px 28px;
    cursor: pointer;
    transition: opacity 0.15s;
    width: 100%;
  }

  .ach-notify-btn:hover {
    opacity: 0.9;
  }
`;

/**
 * AchievementNotification
 *
 * Props:
 *   achievement  – { title, description, icon } object, or null/undefined to hide
 *   onClose      – called when the user dismisses or auto-dismiss fires
 *   autoDismiss  – ms before auto-close (default 5000, pass 0 to disable)
 */
const AchievementNotification = ({ achievement, onClose, autoDismiss = 0 }) => {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (!achievement) return;
    setExiting(false);

    if (autoDismiss > 0) {
      const timer = setTimeout(() => handleClose(), autoDismiss);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [achievement]);

  const handleClose = () => {
    setExiting(true);
    setTimeout(() => {
      onClose?.();
    }, 350);
  };

  if (!achievement) return null;

  return createPortal(
    <>
      <style>{styles}</style>
      <div className="ach-notify-overlay">
        <div
          className={`ach-notify-backdrop ${exiting ? 'exiting' : ''}`}
          onClick={handleClose}
        />
        <div className={`ach-notify-card ${exiting ? 'exiting' : ''}`}>
          <div className="ach-notify-label">Achievement Unlocked</div>

          <div className="ach-notify-icon-wrap">
            <div className="ach-notify-burst" />
            <div className="ach-notify-burst ach-notify-burst-2" />
            <div className="ach-notify-icon-bg">
              {achievement.icon || '🏅'}
            </div>
          </div>

          <div className="ach-notify-title">{achievement.title}</div>
          {achievement.description && (
            <div className="ach-notify-desc">{achievement.description}</div>
          )}

          {achievement.xp_reward && (
              <div className="ach-notify-xp">+{achievement.xp_reward} XP</div>
          )}

          <button className="ach-notify-btn" onClick={handleClose}>
            Nice!
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default AchievementNotification;